import classNames from 'classnames';
import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import type { ShotFeed } from '../../stores/types/shots';
import { FEED_TYPES } from '../../stores/reducers/shots';
import type {
	MediaItem,
	PresentedMediaItem,
} from '../../stores/types/media';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import { getVideoTimeAsHHMMSS, isVideo } from '../../utils/utils';
import { videoActionCommands } from '../../utils/video-machine';
import useShotMemos from '../../hooks/use-shot-memos';
import { findMediaPageBy } from '../../utils/media';
import { useGetMediaControls } from '../../contexts/media-controls';
import { GreenroomState } from '../../stores/types/types';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { saveMediaPage } from '../../stores/actions/media';
import * as MediaActions from '../../stores/actions/media';
import PlayButton from '../media/controls/play-button';
import { AUDIO_TYPES } from '../../types/constants';
import usePptxDeckAnimations from '../../hooks/use-pptx-deck-animations';
import { pptxDeckSlideHasBuilds, PptxDeckSlideInfo } from '../../utils/pptx-deck-animations-reconcile';

// Stable empty-slides reference so the pptx-slide selector fallback doesn't churn renders.
const EMPTY_SLIDES: PptxDeckSlideInfo[] = [];

const {
	MUTE,
	UNMUTE,
	PLAY,
	PAUSE,
	SCRUB,
	STOPSCRUBBING,
	VOLUME,
} = videoActionCommands;

interface MyProps {
	shotFeed: ShotFeed;
	mediaItem: MediaItem;
	mouseIsOver: boolean;
	totalPages: number;
	mediaName?: string;
	isCurrentActiveShot: boolean;
	fadeMs?: number;
}

export default function VideoThumbnailMouseover(props: MyProps) {
	const {
		shotFeed,
		mediaItem,
		mouseIsOver,
		totalPages,
		mediaName,
		isCurrentActiveShot,
		fadeMs = 250,
	} = props;

	const dispatch = useDispatch();

	const presentedMedia = useSelector(
		(state: GreenroomState) => state.MediaReducer.presentedMedia,
	);
	const contentTemplates = useSelector(
		(state: GreenroomState) => state.GeneratedContentReducer.contentTemplates,
	);
	const mediaPages = useSelector(
		(state: GreenroomState) => state.MediaReducer.mediaPages,
	);
	const roomState = useSelector(
		(state: GreenroomState) => state.MeetingReducer.roomState,
	);
	const showbuilderMedia = useSelector(
		(state: GreenroomState) => state.MediaReducer.showbuilderMedia,
	);
	const meeting = useSelector(
		(state: GreenroomState) => state.SessionReducer.meeting,
	);
	const token = useSelector(
		(state: GreenroomState) => state.SessionReducer.token,
	);
	const session = useSelector(
		(state: GreenroomState) => state.SessionReducer.session,
	);

	const presentedMediaItem = presentedMedia.find((p) => p.mediaId === shotFeed.media);
	const { currentShot, getShotDuration } = useShotMemos();
	const presentedMediaControls = useGetMediaControls(`${mediaItem?.id}`);
	const mediaPagesForMediaItem = mediaPages.find((m) => m.mediaId === mediaItem?.id);
	const [mediaPageVolume, setMediaPageVolume] = useState<number>(
		mediaPagesForMediaItem?.volume ?? 1,
	);

	const timeBadgeRef = useRef<HTMLDivElement>(null);
	const mediaControlsRef = useRef<HTMLDivElement>(null);

	const offShotSend = (command: string, data?: any) => {
		if (
			!mediaPagesForMediaItem
			|| !meeting?.meeting_id
			|| !meeting?.meeting
			|| !token
			|| !session
		) {
			return;
		}

		const updatedMediaPage = { ...mediaPagesForMediaItem };
		switch (command) {
			case UNMUTE: {
				updatedMediaPage.muted = false;
				break;
			}
			case MUTE: {
				updatedMediaPage.muted = true;
				break;
			}
			case VOLUME: {
				if (!data?.doFireSync) {
					setMediaPageVolume(data.shownVolume);
					return;
				}
				updatedMediaPage.volume = data.shownVolume;
				updatedMediaPage.muted = false;
				break;
			}
			default: {
				console.log('unsupported send command: ', command);
			}
		}

		try {
			dispatch(saveMediaPage(
				token,
				meeting.meeting,
				updatedMediaPage,
				session,
			));
		} catch (error) {
			console.error('error updating media page', error);
		}
	};

	const switchPage = (newPage: number) => {
		if (presentedMediaItem) {
			const newVideoState = { paused: true, timeElapsed: 0, volume: 1 };
			dispatch(
				MediaActions.setPresentedMediaPage(shotFeed.media ?? 0, newPage, newVideoState),
			);
		} else {
			// Copy any data from the media item that is used during presenting
			const mediaToPresent: PresentedMediaItem = {
				mediaId: mediaItem.id,
				currentPage: newPage ?? 1,
			};
			dispatch(MediaActions.setSelectedPage(1));
			dispatch(MediaActions.addPresentedMedia(mediaToPresent));
		}
	};

	const showbuilderMediaItem = useMemo((): PresentedMediaItem | null => {
		if (roomState === ROOM_STATES.EDITING && mediaItem) {
			const foundItem = showbuilderMedia.find(
				({ mediaId }) => mediaId === shotFeed.media,
			);

			if (foundItem) {
				return foundItem;
			}
		}

		return null;
	}, [mediaItem, roomState, shotFeed, showbuilderMedia]);

	const currentPageNumber = useMemo((): number => {
		if (roomState === ROOM_STATES.EDITING) {
			return showbuilderMediaItem?.currentPage ?? 1;
		}

		return presentedMediaItem?.currentPage ?? 1;
	}, [
		presentedMediaItem?.currentPage,
		roomState,
		showbuilderMediaItem?.currentPage,
	]);

	const { hasAnimations } = usePptxDeckAnimations(mediaItem.id);
	const pptxSlides = useSelector(
		(state: GreenroomState) => state.PptxDeckAnimationsReducer.info[mediaItem.id]?.slides,
	) ?? EMPTY_SLIDES;
	// pptx decks have no meaningful num_pages; use the deck's real slide count instead.
	const deckTotal = hasAnimations ? pptxSlides.length : totalPages;
	const showAnimationIcon = hasAnimations && pptxDeckSlideHasBuilds(pptxSlides, currentPageNumber);

	const offShotPreviousPage = () => {
		if (currentPageNumber <= 1) {
			return;
		}
		switchPage(currentPageNumber - 1);
	};

	const offShotNextPage = () => {
		if (currentPageNumber >= totalPages) {
			return;
		}
		switchPage(currentPageNumber + 1);
	};

	const offShotGoToPage = (page: number) => {
		if (page < 1 || page > totalPages) {
			return;
		}
		switchPage(page);
	};

	const isInCurrentShot = useMemo(() => currentShot.shot_data.shotFeeds.find(
		({ media }) => media === shotFeed.media,
	), [presentedMediaItem, shotFeed.media]);

	const offShotObject = { // use the mediaPage data to backfill the data
		state: null,
		elapsed: 0,
		send: offShotSend,
		previousPage: offShotPreviousPage,
		nextPage: offShotNextPage,
		goToPage: offShotGoToPage,
	};

	const {
		state,
		send,
		previousPage,
		nextPage,
		goToPage,
	} = isInCurrentShot ? presentedMediaControls ?? offShotObject : offShotObject;

	const {
		paused,
		muted,
		duration,
		shownVolume,
		elapsed,
	} = state || {
		paused: true,
		duration: 0,
		muted: mediaPagesForMediaItem?.muted ?? false,
		shownVolume: mediaPageVolume ?? 1,
		elapsed: 0,
	};

	const [durationOfShot, setDurationOfShot] = useState<number>(0);

	useEffect(() => {
		if (duration) {
			setDurationOfShot(duration);
		} else {
			// eslint-disable-next-line @typescript-eslint/no-shadow
			getShotDuration(mediaItem?.id ?? 0).then((duration) => {
				setDurationOfShot(duration);
			});
		}
	}, [duration, mediaItem?.id]);

	const urlOfCurrentPage = useMemo(() => {
		const currentPage = roomState === ROOM_STATES.EDITING
			? showbuilderMediaItem?.currentPage ?? 1
			: presentedMediaItem?.currentPage ?? 1;
		const imageArray = mediaItem?.media_data.image_array ?? [''];
		return imageArray[currentPage - 1] ?? '';
	}, [
		mediaItem?.media_data.image_array,
		presentedMediaItem?.currentPage,
		roomState,
		showbuilderMediaItem?.currentPage,
	]);

	const currentMediaPage = useMemo(
		() => findMediaPageBy(mediaPages, urlOfCurrentPage, presentedMediaItem?.mediaId ?? showbuilderMediaItem?.mediaId),
		[mediaPages, urlOfCurrentPage, presentedMediaItem?.mediaId, showbuilderMediaItem?.mediaId],
	);

	const volumeScrubCss = useMemo(() => ({
		background: `linear-gradient(to right, white 0%, white ${
			muted ? 0 : Number(shownVolume) * 100
		}%, rgba(255, 255, 255, 0.3) ${
			muted ? 0 : Number(shownVolume) * 100
		}%, rgba(255, 255, 255, 0.3) 100%)`,
	}), [shownVolume, muted]);

	const currentPageIsVideo: boolean = useMemo(() => (
		urlOfCurrentPage
			? isVideo(urlOfCurrentPage) || mediaItem.type === 'hls' || AUDIO_TYPES.includes(mediaItem?.type ?? '')
			: false
	), [urlOfCurrentPage]);

	const hasAudio: boolean = useMemo(() => {
		const items = contentTemplates.filter(x => x.type == mediaItem.template_type);
		//a non-editable stylepack asset has audio
		if (items.length && items[0].data?.strict) {
			return true;
		}

		if (mediaItem && mediaItem.template_type && shotFeed.feedType == FEED_TYPES.MEDIA) {
			return false;
		}

		return true;
	}, [mediaItem, shotFeed])

	const pageCountBadge = useMemo((): JSX.Element => {
		if (currentPageNumber && deckTotal > 1) {
			return (
				<div className={classNames(
					'media-thumb-time small-gap',
				)}>
					{showAnimationIcon
						&& <Icon name={ICONS.ANIMATION} size={12} color={COLORS.WHITE} />
					}
					<span>{currentPageNumber}</span>
					<span className="spacer">/</span>
					<span>{deckTotal}</span>
				</div>
			);
		}

		return <></>;
	}, [currentPageNumber, deckTotal, showAnimationIcon]);

	const timeLeft = useMemo(
		() => durationOfShot - (elapsed ?? 0),
		[durationOfShot, elapsed],
	);

	const buttonIsDisabled = useMemo(
		() => !isCurrentActiveShot
		&& !isInCurrentShot
		&& currentPageIsVideo,
		[isCurrentActiveShot, isInCurrentShot, currentPageIsVideo, mediaItem.type],
	);

	function videoPlayPause() {
		if (send) {
			send(
				paused || timeLeft <= 0.9 ? PLAY : PAUSE,
				{
					from: 'video-thumbnail pause onClick',
					doFireSync: true,
				},
			);
		} else {
			console.log('no send');
		}
	}

	return (
		<div className={classNames(
			'media-thumb-container',
			{ hover: mouseIsOver },
		)}>
			<CSSTransition
				in={!mouseIsOver}
				nodeRef={timeBadgeRef}
				timeout={fadeMs}
				classNames="fade-in-out"
				unmountOnExit
			>
				<div
					className={classNames(
						'media-thumb-badge-row',
						{ 'is-audio-only': shotFeed.audioOnly },
					)}
					ref={timeBadgeRef}
				>
					{pageCountBadge}
					{currentPageIsVideo && durationOfShot ? (
						<div className="media-thumb-time">
							{getVideoTimeAsHHMMSS(durationOfShot)}
						</div>
					) : (
						<></>
					)}
				</div>
			</CSSTransition>
			<CSSTransition
				in={mouseIsOver}
				nodeRef={mediaControlsRef}
				timeout={fadeMs}
				classNames="fade-in-out"
				unmountOnExit
			>
				<div ref={mediaControlsRef}>
					<div className={classNames(
						'media-thumb-badge-row',
						{ 'is-audio-only': shotFeed.audioOnly },
					)}>
						{pageCountBadge}
						{currentPageIsVideo && durationOfShot ? (
							<div className="media-thumb-time">
								{timeLeft >= 1 ? '-' : ''}
								{timeLeft >= 0 ? getVideoTimeAsHHMMSS(timeLeft) : '0:00'}
							</div>
						) : (
							<></>
						)}
					</div>
					<div className="media-thumb-controls">
						<div
							className={classNames('media-thumb-controls-icon gray', {
								disabled: buttonIsDisabled && totalPages <= 1,
							})}
							onClick={() => {
								if (buttonIsDisabled && totalPages <= 1) {
									return;
								}

								if (totalPages <= 1) {
									if (send) {
										send(SCRUB, {
											currentTime: 0,
											from: 'video-thumbnail reset onClick',
										});
										send(STOPSCRUBBING);
									} else {
										console.log('no send');
									}
								} else {
									previousPage?.();
								}
							}}
						>
							{totalPages <= 1 ? (
								<Icon
									name={ICONS.PLAY_RESET}
									color={COLORS.TEXT_GRAY_200}
									size={14}
								/>
							) : (
								<Icon
									name={ICONS.KEYBOARD_ARROW_LEFT}
									color={COLORS.TEXT_GRAY_200}
									size={14}
								/>
							)}
						</div>
						{currentPageIsVideo
							? <PlayButton
								paused={paused || timeLeft <= 0.9}
								videoPlayPause={videoPlayPause}
								disabled={buttonIsDisabled}
								className={'media-thumb-controls-icon'}
							/>
							: <div
								className={classNames('media-thumb-controls-icon gray', { disabled: buttonIsDisabled })}
								onClick={() => goToPage?.(1)}
							>
								<Icon
									name={ICONS.PLAY_RESET}
									color={COLORS.TEXT_GRAY_200}
									size={14}
								/>
							</div>
							}
						{hasAudio && (<div
							className="media-thumb-controls-icon gray"
							onClick={() => {
								if (totalPages <= 1) {
									if (send) {
										send(
											muted ? UNMUTE : MUTE,
											{
												from: 'video-thumbnail mute onClick',
												doFireSync: true,
												currentMediaPage,
											},
										);
									} else {
										console.log('no send');
									}
								} else {
									nextPage?.();
								}
							}}
						>
							{totalPages <= 1 ? (
								<Icon
									name={muted ? ICONS.MUTE_SOUND : ICONS.SOUND}
									color={COLORS.TEXT_GRAY_200}
									size={14}
								/>
							) : (
								<Icon
									name={ICONS.KEYBOARD_ARROW_RIGHT}
									color={COLORS.TEXT_GRAY_200}
									size={14}
								/>
							)}
						</div>)}
					</div>
					{currentPageIsVideo && hasAudio? (
						<div className="media-thumb-volume">
							<Icon
								name={ICONS.SOUND}
								color={COLORS.WHITE}
								size={13}
							/>
							<input
								type="range"
								className="scrub-click-area"
								onMouseDown={() => {
									if (send) {
										if (muted) {
											send(VOLUME, {
												shownVolume: 0,
												doFireSync: true,
												from: 'volume onMouseDown',
												currentMediaPage,
											});
										}
									} else {
										console.log('no send');
									}
								}}
								onChange={(e) => {
									if (send) {
										send(VOLUME, {
											shownVolume: Number(e.target.value),
											from: 'video-thumbnail volume onChange',
											currentMediaPage,
										});
									} else {
										console.log('no send');
									}
								}}
								onMouseUp={() => {
									if (send) {
										setTimeout(() => {
											send(VOLUME, {
												shownVolume: Number(shownVolume),
												doFireSync: true,
												from: 'video-thumbnail volume onMouseUp',
												currentMediaPage,
											});
										}, 100);
									} else {
										console.log('no send');
									}
								}}
								value={muted ? 0 : shownVolume}
								step={0.01}
								min={0}
								max={1}
								style={volumeScrubCss}
							>
							</input>
						</div>
					) : (
						<div className="media-name no-background">
							<label className="font-size-14 font-weight-heavy">
								{mediaName}
							</label>
						</div>
					)}
				</div>
			</CSSTransition>
		</div>
	);
}
